import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastState {
  toasts: Toast[];
  timeouts: Map<string, ReturnType<typeof setTimeout>>;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  timeouts: new Map(),

  addToast: (toast) => {
    const id = crypto.randomUUID();
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove after duration
    if (newToast.duration && newToast.duration > 0) {
      const timeoutId = setTimeout(() => {
        const { timeouts } = get();
        timeouts.delete(id);
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
          timeouts: new Map(timeouts),
        }));
      }, newToast.duration);

      // Store timeout ID for cleanup
      const { timeouts } = get();
      timeouts.set(id, timeoutId);
      set({ timeouts: new Map(timeouts) });
    }
  },

  removeToast: (id) => {
    // Clear the timeout if it exists
    const { timeouts } = get();
    const timeoutId = timeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeouts.delete(id);
    }

    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
      timeouts: new Map(timeouts),
    }));
  },

  clearToasts: () => {
    // Clear all timeouts
    const { timeouts } = get();
    timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    timeouts.clear();

    set({ toasts: [], timeouts: new Map() });
  },
}));

// Convenience hook
export function useToast() {
  const { addToast, removeToast, clearToasts } = useToastStore();

  const success = useCallback((title: string, message?: string) =>
    addToast({ type: 'success', title, message }), [addToast]);
  
  const error = useCallback((title: string, message?: string) =>
    addToast({ type: 'error', title, message }), [addToast]);
  
  const warning = useCallback((title: string, message?: string) =>
    addToast({ type: 'warning', title, message }), [addToast]);
  
  const info = useCallback((title: string, message?: string) =>
    addToast({ type: 'info', title, message }), [addToast]);

  return useMemo(() => ({
    toast: addToast,
    success,
    error,
    warning,
    info,
    remove: removeToast,
    clear: clearToasts,
  }), [addToast, success, error, warning, info, removeToast, clearToasts]);
}
